import React from "react";
import Modal from "react-bootstrap/Modal";
import { FiX } from "react-icons/fi";
import "./WhatsAppDeliveryNoteSetupGuide.css";

function ExternalLink({ href, children }) {
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
}

export default function WhatsAppDeliveryNoteSetupGuide({ show, onHide, businessUnitName }) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="waDnGuide"
      lang="es"
      aria-labelledby="wa-dn-guide-title"
      aria-describedby="wa-dn-guide-description"
    >
      <Modal.Header>
        <div>
          <div className="waDnGuideEyebrow">WhatsApp Delivery Notes</div>
          <h2 id="wa-dn-guide-title">Configurar un número y sus remitentes</h2>
          <p id="wa-dn-guide-description" className="waDnGuideIntro">
            Configura un número receptor nuevo o reutiliza uno existente, y autoriza los
            teléfonos que pueden enviar albaranes a esta Business Unit.
          </p>
        </div>
        <button type="button" className="waDnGuideClose" onClick={onHide} aria-label="Cerrar guía">
          <FiX aria-hidden="true" />
        </button>
      </Modal.Header>

      <Modal.Body>
        <p className="waDnGuideContext">
          <strong>Business Unit seleccionada:</strong> {businessUnitName || "Ninguna todavía"}
        </p>
        <p className="waDnGuideNote">
          <strong>Cómo se elige la Business Unit:</strong> cada mensaje se identifica por
          el <strong>Phone number ID</strong> receptor y el teléfono del remitente autorizado.
          Varias BUs pueden compartir receptor, pero una misma combinación de receptor y
          remitente solo puede asignarse a una BU. Un remitente puede enviar a otra BU si
          utiliza un receptor distinto.
        </p>
        <p className="waDnGuideNote">
          <strong>Obligatorio para cada número nuevo:</strong> el usuario del sistema que es
          propietario del token debe tener acceso tanto al número de WhatsApp como a su
          cuenta de mensajes. Que otro número funcione con ese token no confirma estos permisos.
        </p>

        <ol className="waDnGuideSteps">
          <li>
            <h3>Elige un receptor nuevo o compartido</h3>
            <p>Si reutilizas un receptor ya configurado, usa su mismo
              {" "}<strong>Receiving phone number (display)</strong>, <strong>Phone number ID (receiver)</strong> y
              {" "}<strong>WhatsApp Business Account ID</strong>. No necesitas registrar otro
              número para compartirlo entre BUs.</p>
            <p>En ese caso, continúa con <strong>Configura el receptor en administración y los remitentes en Settings</strong>
              {" "}y comprueba el token y la suscripción existentes. Si el receptor es nuevo,
              sigue primero los pasos de Meta que aparecen a continuación.</p>
          </li>
          <li>
            <h3>Desde «Mis apps», entra en Pilcore Delivery Notes</h3>
            <p>Abre <ExternalLink href="https://developers.facebook.com/apps/">Mis apps de Meta</ExternalLink>
              {" "}y pulsa la tarjeta <strong>Pilcore Delivery Notes</strong>.</p>
            <p>En <strong>Panel</strong>, pulsa <strong>Personalizar el caso de uso
              "Conectarte con los clientes a través de WhatsApp"</strong>.
              En el menú de ese caso de uso, entra en <strong>Configuración básica
              → Paso 2: Configuración de producción</strong>.</p>
          </li>
          <li>
            <h3>Pulsa «Agregar número nuevo»</h3>
            <p>Despliega <strong>Registra tu número de teléfono de WhatsApp</strong>.
              Debajo del teléfono que ya funciona, pulsa <strong>Agregar número nuevo</strong>.</p>
            <p>Rellena <strong>Nombre visible de WhatsApp Business</strong> con el nombre del
              establecimiento, elige su <strong>Zona horaria</strong> y <strong>Categoría</strong>.
              La descripción es opcional. Pulsa <strong>Siguiente</strong>.</p>
            <p>Continúa por <strong>Agregar número</strong> con el teléfono nuevo y por
              <strong> Verificar número</strong> con el código que recibas por SMS o llamada.
              Al terminar, comprueba que el nuevo teléfono aparezca como <strong>Registrado</strong>.</p>
          </li>
          <li>
            <h3>Copia los datos del nuevo número</h3>
            <p>En ese mismo apartado, copia el <strong>Identificador de número de teléfono</strong>
              {" "}que aparece debajo del <strong>nuevo teléfono</strong>: irá en <strong>Phone number ID</strong>.</p>
            <p>Copia el <strong>Identificador de la cuenta de WhatsApp Business</strong>
              {" "}o <strong>Messaging Account ID</strong> asociado al nuevo teléfono:
              irá en <strong>WhatsApp Business Account ID</strong>. Puede ser distinto al
              del número anterior. Identifica la cuenta por su ID, porque varias pueden
              tener el mismo nombre, por ejemplo <strong>Pilcore</strong>.</p>
          </li>
          <li>
            <h3>Asigna el usuario del sistema al número de WhatsApp</h3>
            <p>Abre <ExternalLink href="https://business.facebook.com/">Meta Business Suite</ExternalLink>,
              selecciona el portfolio comercial propietario del número y pulsa
              {" "}<strong>Configuración</strong> (el engranaje de la barra lateral).
              También puedes abrir directamente
              {" "}<ExternalLink href="https://business.facebook.com/settings/">Configuración del negocio</ExternalLink>.
              Este paso se hace en Meta Business Suite, fuera de «Configuración de la app»
              del panel de desarrolladores.</p>
            <p className="waDnGuideNote">
              <strong>Ruta del número:</strong> Configuración → Cuentas → Cuentas de WhatsApp
              → selecciona el teléfono nuevo → Asignar personas.
            </p>
            <p>Selecciona el <strong>usuario del sistema propietario del token</strong>
              {" "}(por ejemplo, <strong>Employee</strong>), activa <strong>Acceso total</strong>
              y confirma la asignación. Puedes localizar ese usuario en
              {" "}<strong>Configuración → Usuarios → Usuarios del sistema</strong>.</p>
            <p>En la ficha del teléfono, abre la pestaña horizontal <strong>Personas</strong>,
              junto a <strong>Resumen</strong>, y comprueba que ese usuario figure con
              {" "}<strong>Acceso total</strong>. Es la pestaña del número seleccionado;
              el menú lateral «Usuarios → Personas» muestra las personas del negocio.</p>
          </li>
          <li>
            <h3>Asigna también el usuario a la cuenta de mensajes vinculada</h3>
            <p>En la ficha del teléfono nuevo, abre la pestaña horizontal
              {" "}<strong>Messaging accounts</strong> para identificar su cuenta vinculada.
              Después sigue esta ruta:</p>
            <p className="waDnGuideNote">
              <strong>Ruta de la cuenta:</strong> Configuración → Cuentas → Cuentas de mensajes
              (Messaging accounts) → selecciona la cuenta vinculada → Asignar personas.
            </p>
            <p>Antes de asignar, comprueba que el <strong>Identificador</strong> de la cuenta
              coincida con el <strong>WhatsApp Business Account ID / Messaging Account ID</strong>
              del nuevo número. En la pestaña <strong>WhatsApp accounts</strong> de esa cuenta
              puedes comprobar qué teléfono tiene vinculado. No elijas solo por el nombre.</p>
            <p>Selecciona el <strong>mismo usuario del sistema</strong> del token, activa
              {" "}<strong>Acceso total</strong> y confirma. Abre la pestaña horizontal
              {" "}<strong>Personas</strong> de esta cuenta de mensajes y verifica que el usuario
              aparezca con ese acceso. Revisa esta asignación aunque ya la hayas hecho en el teléfono.</p>
            <p>El contador <strong>Full control</strong> indica cuántos usuarios tienen control total:
              no tiene que coincidir con el del número anterior. Lo necesario es que incluya al
              usuario del sistema que utiliza la integración; que solo aparezca tu usuario
              personal no basta.</p>
          </li>
          <li>
            <h3>Configura el receptor en administración y los remitentes en Settings</h3>
            <p>Vuelve a esta pantalla, selecciona la <strong>Business Unit</strong> que debe
              recibir los albaranes y rellena:</p>
            <dl className="waDnGuideFields">
              <dt>Status</dt><dd>Draft mientras preparas la configuración y los remitentes.</dd>
              <dt>Receiving phone number (display)</dt><dd>El teléfono receptor con prefijo, por ejemplo +34600000000.</dd>
              <dt>Phone number ID (receiver)</dt><dd>El ID del receptor, nuevo o compartido.</dd>
              <dt>WhatsApp Business Account ID</dt><dd>El ID de la cuenta del receptor.</dd>
            </dl>
            <p>Deja la versión de API y las opciones de imágenes, PDF y OCR como están.
              Pulsa <strong>Prepare WhatsApp</strong> (o <strong>Save settings</strong> si ya existe)
              {" "}para guardar el receptor. Los tokens y la conexión con Meta se gestionan en este panel.</p>
            <p>En la sesión de Pilcore del usuario, selecciona la BU correspondiente y abre
              {" "}<ExternalLink href="/purchasing/settings/whatsapp">Compras → Settings → Remitentes de WhatsApp</ExternalLink>.
              {" "}Con permiso para actualizar la configuración de compras, pulsa <strong>Añadir remitente</strong>
              {" "}e introduce su <strong>Teléfono internacional</strong> en formato E.164: + y entre 7 y 15 dígitos,
              por ejemplo +34611111111. No se presupone ningún país. El nombre es opcional.
              Marca <strong>Activo</strong> y pulsa <strong>Guardar remitentes</strong>.</p>
            <p className="waDnGuideNote">
              <strong>La autorización se comprueba antes de descargar el archivo o ejecutar OCR.</strong>
              {" "}Los mensajes de remitentes no autorizados se descartan. Si la lista está vacía
              o todos sus remitentes están inactivos, no se procesa ningún albarán para esta BU.
              {" "}La migración no autoriza automáticamente ningún remitente: registra y guarda
              la lista antes de activar la integración.
            </p>
            <p>Comprueba que los remitentes están guardados en Settings antes de activar la integración
              en administración. Guardar el receptor o el token en este panel conserva la lista de remitentes.</p>
            <details>
              <summary>Al guardar aparece un conflicto de remitente (409)</summary>
              <p>La combinación de ese receptor y remitente ya está asignada a otra BU.
                Si el remitente debe pertenecer a la BU actual con ese receptor, pide a un usuario
                con acceso a la otra BU que lo elimine de su lista y guarde allí primero.
                Después vuelve a Settings de esta BU y guarda sus remitentes.</p>
              <p>Si debe seguir enviando a ambas BUs, configura un receptor distinto para una de
                ellas. Cambiar únicamente el nombre del remitente no cambia su asignación.</p>
            </details>
          </li>
          <li>
            <h3>Guarda el token que ya utilizas</h3>
            <p>Puedes reutilizar el mismo token para varios números si su usuario del sistema
              tiene acceso a las cuentas correspondientes y los permisos
              {" "}<code>whatsapp_business_messaging</code> y <code>whatsapp_business_management</code>.</p>
            <p>Pega ese token en <strong>Meta access token → Access token</strong>
              {" "}→ <strong>Save token</strong> → <strong>Test connection</strong>.
              Debe aparecer <strong>connection OK</strong> con el teléfono receptor.
              El <strong>Verify token</strong> del webhook es distinto y no sirve como Access token.</p>
            <p><strong>Test connection</strong> comprueba el acceso al teléfono; no confirma
              por sí solo los permisos sobre la cuenta de mensajes ni la recepción de albaranes.</p>
            <details>
              <summary>No tengo el token a mano</summary>
              <p>Recupéralo de donde lo guardaste; esta pantalla no muestra el token anterior.
                Si no lo conservas, abre <ExternalLink href="https://business.facebook.com/settings/">Configuración del negocio</ExternalLink>
                {" "}→ <strong>Usuarios → Usuarios del sistema</strong> → el usuario que ya utilizas
                {" "}→ <strong>Generar token</strong>. Selecciona la misma aplicación y los permisos
                {" "}<code>whatsapp_business_messaging</code> y <code>whatsapp_business_management</code>.
                Guarda el nuevo token sin revocar el anterior.</p>
            </details>
          </li>
          <li>
            <h3>Comprueba la suscripción de la cuenta nueva al webhook</h3>
            <p>Vuelve a <strong>Pilcore Delivery Notes → Casos de uso → Conectarte con los
              clientes a través de WhatsApp → Configuración básica → Paso 2: Configuración
              de producción</strong>.</p>
            <p>En <strong>Registra tu número de teléfono de WhatsApp</strong>, localiza la cuenta
              del nuevo teléfono por su <strong>Messaging Account ID</strong> y comprueba que
              {" "}<strong>Suscribir webhooks</strong> esté activado para ella. En
              {" "}<strong>Configurar webhooks</strong>, el campo <strong>messages</strong> debe
              aparecer como <strong>Suscritos</strong>.</p>
            <p>La misma aplicación puede atender varias cuentas. Conserva la
              {" "}<strong>Callback URL</strong> y el <strong>Verify token</strong> que ya funcionan;
              comprueba la suscripción de cada cuenta nueva.</p>
          </li>
          <li>
            <h3>Prueba desde un remitente autorizado</h3>
            <p>Comprueba que has guardado la configuración con el remitente activo.
              Cambia <strong>Status</strong> a <strong>Active</strong> y pulsa <strong>Save settings</strong>.
              Desde ese teléfono autorizado, envía una foto o PDF al número receptor.
              Aquí pulsa <strong>Refresh</strong>: debe aparecer <strong>Import created</strong>.</p>
            <p>Para verlo, abre <ExternalLink href="/purchasing/orders/delivery-notes">Compras → Albaranes</ExternalLink>
              {" "}en la Business Unit de esa combinación de receptor y remitente
              {" "}→ <strong>Delivery Note Inbox</strong> (Bandeja de albaranes).</p>
            <details>
              <summary>El número está conectado, pero no aparecen eventos</summary>
              <p>En Pilcore, selecciona la Business Unit correspondiente al receptor y al remitente.
                Comprueba que el teléfono del remitente está autorizado, activo y guardado,
                con su prefijo internacional. Un remitente descartado no llega a la descarga ni al OCR.</p>
              <p>
                En Meta Business Suite, revisa las dos asignaciones:
                {" "}<strong>Cuentas de WhatsApp → teléfono → Personas</strong> y
                {" "}<strong>Cuentas de mensajes → cuenta vinculada → Personas</strong>.
                El usuario del sistema del token debe aparecer en ambas con acceso total.
                Comprueba también los IDs y la suscripción al webhook de esa cuenta.</p>
              <p>Después de corregirlo, envía un nuevo mensaje con una foto o PDF y actualiza
                {" "}<strong>Webhook events</strong>. Los dos ticks de WhatsApp o
                {" "}<strong>connection OK</strong> no sustituyen la comprobación de
                {" "}<strong>Import created</strong> en Pilcore.</p>
            </details>
          </li>
        </ol>

        <p className="waDnGuideNote">
          <strong>Antes de dar el número por configurado:</strong> verifica el usuario del sistema
          en las dos pestañas «Personas», la suscripción de su cuenta al webhook y los remitentes
          activos guardados en la BU. Comprueba una importación desde un remitente autorizado
          cada vez que añadas un número o compartas un receptor con otra BU.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <span className="waDnGuideFooterNote">Cerrar la guía conserva los datos del formulario.</span>
        <button type="button" className="waDnGuideDone" onClick={onHide}>Volver a la configuración</button>
      </Modal.Footer>
    </Modal>
  );
}
